// P57-C — Registre d'icônes de type d'engin.
//
// AVANT : `type_engin.icone` stocke un NOM de classe lucide ("Truck",
// "Crane"…) affiché dans un <select> natif. L'utilisateur ne voyait donc
// AUCUN rendu visuel, et l'icône n'était relue nulle part après création.
//
// Ce module est la SOURCE DE VIE UNIQUE du mapping nom → composant :
//  - le sélecteur de création l'affiche sous forme de grille de glyphes
//  - les listes de types ET d'engins s'en servent pour le rendu
//
// ⚠️ On continue de stocker le NOM en base (et non le SVG) : les données
// existantes référencent ces noms, et le nom est une valeur stable et
// lisible. Changer de représentation casserait le stock.

import {
  Anchor,
  Bike,
  Bolt,
  Boxes,
  Box,
  Building2,
  Bus,
  Car,
  Cog,
  Construction,
  Container,
  Drill,
  Factory,
  Flame,
  Forklift,
  HardHat,
  Hammer,
  Layers,
  Package,
  Plane,
  Ship,
  Ship as TrainIcon, // « Train » n'existe pas dans lucide → icône maritime
  Truck,
  Warehouse,
  Wrench,
  Zap,
} from "lucide-react";

export type LucideIcon = React.ComponentType<{
  className?: string;
  /** attributs SVG additionnels (title, aria-*, data-*) */
  [key: string]: unknown;
}>;

/**
 * Les 30 noms historiques + les équivalents des icônes lucide qui ont été
 * renommées ou supprimées.
 *
 * ⚠️ Trois noms de la liste d'origine n'existent PAS dans lucide-react
 * (`Crane`, `Train`, `Tool`) : ils pointent vers l'équivalent le plus
 * proche plutôt que de tomber sur le repli générique, pour que les lignes
 * de base créées avant la migration restent visuellement cohérentes.
 */
export const ENGINE_ICON_MAP: Record<string, LucideIcon> = {
  // -- véhicules
  Truck,
  Car,
  Bus,
  Bike,
  Plane,
  Ship,
  "Train": TrainIcon,
  Forklift,

  // -- chantier / industry
  Construction,
  Container,
  Factory,
  Warehouse,
  HardHat,
  Hammer,
  Anchor,

  // --.generic
  Boxes,
  Box,
  Package,
  Layers,

  // -- maintenance
  Wrench,
  Bolt,
  Cog,
  Drill,
  Flame,
  Zap,

  // -- compat : noms historiques sans équivalent direct
  Building2, // "Settings" → on garde un bâtiment (parc / site)
  "Crane": Construction,
  "Tool": Wrench,
  "Settings": Cog,
  // -- doublons « XxxIcon » de l'ancienne liste (lucide exporte ces
  //    composants SANS le suffixe ; l'app les proposait malgré tout, donc
  //    des lignes en base peuvent porter ces valeurs)
  "WrenchIcon": Wrench,
  "ContainerIcon": Container,
  "BoltIcon": Bolt,
  "Tractor": Boxes,

  // ── Alias français ────────────────────────────────────────────────────
  // ⚠️ DECOUVERT : le seed historique stocke des noms FRANÇAIS
  // ("bus", "camion", "grue", "manutention", "remorque"…), alors que
  // l'app proposait des noms lucide anglais. Résultat : les 8 types
  // par défaut affichaient tous l'icône de repli (clé), et l'icône
  // paraissait « ne pas marcher ». On mappe les deux conventions —
  // le seed est corrigé en parallèle, mais les lignes déjà en base
  // gardent leurs valeurs FR.
  bus: Bus,
  voiture: Car,
  camion: Truck,
  grue: Construction,
  grue_auxiliaire: Construction,
  manutention: Forklift,
  remorque: Container,
  tracteur: "Tractor" as unknown as LucideIcon,
  benne: Container,
  generateur: Cog,
  tracteur_agricole: Boxes,
};

/** Icône de repli : nom inconnu (donnée ancienne, faute de frappe, icône
 *  lucide supprimée dans une future version). */
export const FALLBACK_ICON: LucideIcon = Wrench;

/** Liste des noms proposés dans le sélecteur, dans un ordre lisible. */
export const ENGINE_ICON_NAMES: string[] = [
  "Truck", "Car", "Bus", "Bike", "Plane", "Ship", "Train", "Forklift",
  "Construction", "Container", "Factory", "Warehouse", "HardHat", "Hammer",
  "Anchor",
  "Boxes", "Box", "Package", "Layers",
  "Wrench", "Bolt", "Cog", "Drill", "Flame", "Zap",
  "Building2",
  // Noms historiques conservés en base → doivent rester sélectionnables
  "Crane", "Tool", "Settings", "Tractor",
  // Doublons historiques « XxxIcon » : conservés car présents en base
  "WrenchIcon", "ContainerIcon", "BoltIcon",
  // Alias français encore présents en base
  "bus", "voiture", "camion", "grue", "grue_auxiliaire",
  "manutention", "remorque", "tracteur", "benne", "generateur",
  "tracteur_agricole",
];

export function getEngineIcon(name?: string | null): LucideIcon {
  if (!name) return FALLBACK_ICON;
  return ENGINE_ICON_MAP[name] ?? FALLBACK_ICON;
}

/**
 * Rend l'icône d'un type d'engin.
 *
 * `title` positionne une infobulle : l'utilisateur voit le NOM qu'il a
 * choisi, ce qui aide à retrouver un type dans une grille de 30 icônes
 * dont plusieurs se ressemblent (Car/Bus/Truck…).
 */
export function EngineIcon({
  name,
  className = "h-4 w-4",
  title,
}: {
  name?: string | null;
  className?: string;
  title?: boolean;
}) {
  const Icon = getEngineIcon(name);
  return (
    <Icon
      className={className}
      aria-hidden={title ? undefined : true}
      title={title && name ? String(name) : undefined}
    />
  );
}